import { Button, ImprTooltip, Modal } from '@imprivata-cloud/components';
import { Col, Form, Row } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIdpConfigSAML, idpSave, updateIdpConfigSAML } from '../../../api/portalServices';
import { idpGet } from '../../../api/commonServices';
import type { IdpConfigGetResponse, IdpMetadataInfo } from '../../../api/types';
import EditIcon from '../../../assets/icons/edit.svg?react';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import './Integrations.less';
import { withRole } from '../../../common/authorization/Roles';
import { useSession } from '../../../common/authorization/context/Session';
import { IntegrationToken, TokenType } from '../../../common/integration-token/IntegrationToken';
import IdpInfoForm, { type FormValues, IdpFormFieldNames } from '../../../components/idpInfoForm/IdpInfoForm';
import { ErrorCode } from '../../../errorHandler/constants';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { getSPMetadataURL } from '../../../utils/utils';
import { ROLE_TYPE_ADMIN } from '../../constants';

const Integrations = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [idpMetadataInfoList, setIdpMetadataInfoList] = useState<IdpMetadataInfo[]>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [initialSamlConfig, setInitialSamlConfig] = useState<IdpConfigGetResponse>();
  const [idpForm] = Form.useForm<FormValues>();
  const { tenantId } = useSession();

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const idp = await idpGet();
      setIdpMetadataInfoList(idp?.idpMetadataInfoList);
      const metadata = idp?.idpMetadataInfoList?.[0];
      idpForm.setFieldValue(IdpFormFieldNames.NAME, metadata?.idpDisplayName);
      idpForm.setFieldValue(IdpFormFieldNames.URL, metadata?.metadataUrl);
      idpForm.setFieldValue(IdpFormFieldNames.DOMAIN, idp?.domainNames?.[0]);
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      emitError(new AppError(e as ApiError));
    }
  }, [idpForm.setFieldValue, emitError, clearErrorNotifications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function closeModal() {
    if (idpForm.isFieldsTouched()) {
      await fetchData();
    }
    setIsModalVisible(false);
  }

  const idpFormOnFinish = async (formValues: FormValues) => {
    const idpMetadataInfo = idpMetadataInfoList?.[0];
    try {
      setIsLoading(true);
      clearErrorNotifications();
      const metadataUrl = idpMetadataInfo?.metadataUrl || formValues.IdPURL;
      const rawMetadata = formValues.IdPXML || undefined;
      const idpSaveRes = await idpSave({
        metadataId: idpMetadataInfo?.idpMetadataId,
        displayName: formValues.IdPName,
        domain: formValues.IdPDomain,
        metadata:
          metadataUrl || rawMetadata
            ? {
                metadataUrl,
                rawMetadata,
              }
            : undefined,
      });
      await updateSamlGroups(formValues, idpSaveRes.metadataId);
      await closeModal();
    } catch (error) {
      const e = error as ApiError;
      if (e?.code === ErrorCode.ALREADY_EXISTS) {
        e.code = ErrorCode.DOMAIN_ALREADY_EXISTS;
      }
      emitError(new AppError(e));
    } finally {
      setIsLoading(false);
    }
  };

  const updateSamlGroups = async (values: FormValues, metadataId: string) => {
    const groupsChanged =
      values?.AdminGroupsName !== initialSamlConfig?.samlGroupAttrName ||
      values?.AdminGroupsValues !== initialSamlConfig?.samlGroupAttrValues.join(', ');
    if (groupsChanged) {
      try {
        const response = await updateIdpConfigSAML(
          {
            idpMetadataId: metadataId ?? idpMetadataInfoList?.[0]?.idpMetadataId,
            idpConfigId: initialSamlConfig?.idpConfigId,
            samlGroupAttrName: values.AdminGroupsName || '',
            samlGroupAttrValues: (values.AdminGroupsValues ?? '')
              .split(',')
              .map((value: string) => value.trim())
              .filter((value) => value !== ''),
          },
          tenantId || '',
        );
        console.log('Config updated:', response);
      } catch (e) {
        emitError(new AppError(e as ApiError));
      }
    }
  };

  const handleEditSamlConfig = async () => {
    try {
      const samlConfig = await getIdpConfigSAML(
        { idpMetadataId: idpMetadataInfoList?.[0]?.idpMetadataId || '' },
        tenantId || '',
      );
      idpForm.setFieldValue(IdpFormFieldNames.GROUPS_NAME, samlConfig.samlGroupAttrName);
      idpForm.setFieldValue(IdpFormFieldNames.GROUPS_VALUES, samlConfig.samlGroupAttrValues.join(', '));
      setInitialSamlConfig(samlConfig);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
    setIsModalVisible(true);
  };

  return (
    <>
      <div className="integrations-container">
        <Row>
          <Col sm={24} md={24} lg={24} className="setup-content p-xxxl">
            <h2>{t('portal.settings.integrations.header')}</h2>
            {idpMetadataInfoList?.[0] ? (
              <div className="idp-row">
                <div>{idpMetadataInfoList?.[0]?.idpDisplayName}</div>
                <ImprTooltip title={t('portal.settings.integrations.tooltip')}>
                  <Button
                    disabled={isLoading}
                    type="text"
                    icon={<EditIcon fill={'#1474CE'} />}
                    onClick={handleEditSamlConfig}
                    aria-label="edit"
                  />
                </ImprTooltip>
              </div>
            ) : (
              <div className="idp-row">
                <Button disabled={isLoading} type="link" onClick={() => setIsModalVisible(true)}>
                  {t('portal.settings.integrations.no-idp')}
                </Button>
              </div>
            )}
          </Col>
        </Row>
        <div className="divider" />
        <Row className="">
          <Col sm={24} md={24} lg={24} className="setup-content p-xxxl">
            <h2>{t('portal.settings.integrations.eam-integration-token.title')}</h2>
            <IntegrationToken type={TokenType.EAM} isSetup={false} />
          </Col>
        </Row>
        <div className="divider" />
        <Row>
          <Col sm={24} md={24} lg={24} className="setup-content p-xxxl">
            <h2>{t('portal.settings.integrations.vpam-integration-token.title')}</h2>
            <IntegrationToken type={TokenType.VPAM} isSetup={false} />
          </Col>
        </Row>
        <div className="divider" />
        <Row className="">
          <Col sm={24} md={24} lg={24} className="setup-content p-xxxl">
            <h2>{t('portal.settings.integrations.aip-integration-token.title')}</h2>
            <IntegrationToken type={TokenType.AIP} isSetup={false} />
          </Col>
        </Row>
      </div>
      <Modal
        title={t('portal.settings.integrations.modal.title', {
          mode: idpMetadataInfoList?.[0]?.idpMetadataId ? t('common.edit') : t('common.add'),
        })}
        open={isModalVisible}
        width={825}
        destroyOnClose
        onCancel={closeModal}
        footer={null}
      >
        <IdpInfoForm
          form={idpForm}
          spMetadataURL={getSPMetadataURL(tenantId)}
          modalOnClose={closeModal}
          onFinish={idpFormOnFinish}
          isLoading={isLoading}
          editMode={!!idpMetadataInfoList?.[0]?.idpMetadataId}
        />
      </Modal>
    </>
  );
};

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Integrations);
